import { createApi } from '@reduxjs/toolkit/query/react';

import {
  ClientFilesEndpoints as Endpoints,
  type AddClientFileRequest,
  type AddClientFileResponse,
  type AddClientFilesRequest,
  type AddClientFilesResponse,
  type DeleteClientFileRequest,
  type DeleteClientFileResponse,
  type DeleteClientFilesRequest,
  type DeleteClientFilesResponse,
  type DownloadClientFileRequest,
  type DownloadClientFileResponse,
  type GetClientFileListRequest,
  type GetClientFileListResponse,
  type GetClientFileRequest,
  type GetClientFileResponse,
  type StreamClientFileRequest,
  type StreamClientFileResponse,
} from '@app/api/ClientFiles/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';
import { CLIENT_FILES } from '@app/constants';

export const clientFilesApi2 = createApi({
  reducerPath: 'clientFilesApi2',
  baseQuery: createApiBaseQuery(CLIENT_FILES),
  tagTypes: ['ActionHistory'],
  endpoints: ({ mutation, query }) => ({
    [Endpoints.GetClientFile]: query<
      GetClientFileResponse,
      GetClientFileRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientFile,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetClientFileList]: query<
      GetClientFileListResponse,
      GetClientFileListRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientFileList,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.AddClientFile]: mutation<
      AddClientFileResponse,
      AddClientFileRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.AddClientFile,
        method: Methods.POST,
        params,
        data,
      }),
      invalidatesTags: ['ActionHistory'],
    }),
    [Endpoints.AddClientFiles]: mutation<
      AddClientFilesResponse,
      AddClientFilesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.AddClientFiles,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteClientFile]: mutation<
      DeleteClientFileResponse,
      DeleteClientFileRequest
    >({
      query: (params) => ({
        url: Endpoints.DeleteClientFile,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.DeleteClientFiles]: mutation<
      DeleteClientFilesResponse,
      DeleteClientFilesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.DeleteClientFiles,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.Download]: query<
      DownloadClientFileResponse,
      DownloadClientFileRequest
    >({
      query: ({ clientId, fileId, ...params }) => ({
        url: `${Endpoints.Download}/${clientId}/${fileId}`,
        method: Methods.GET,
        responseType: 'arraybuffer',
        params,
      }),
    }),
    [Endpoints.Stream]: query<
      StreamClientFileResponse,
      StreamClientFileRequest
    >({
      query: ({ databaseId, clientId, fileExternalId, ...params }) => ({
        url: `${Endpoints.Stream}/${databaseId}/${clientId}/${fileExternalId}`,
        method: Methods.GET,
        responseType: 'arraybuffer',
        params,
      }),
    }),
  }),
});

export const {
  useGetClientFileQuery,
  useLazyGetClientFileQuery,
  useGetClientFileListQuery,
  useLazyGetClientFileListQuery,
  useAddClientFileMutation,
  useAddClientFilesMutation,
  useDeleteClientFileMutation,
  useDeleteClientFilesMutation,
  useDownloadQuery,
  useLazyDownloadQuery,
  useStreamQuery,
  useLazyStreamQuery,
} = clientFilesApi2;
